import { useState, useEffect } from "react";
import { authService } from "../service/AuthService";
import { Transporter } from "../interfaces/transporters.interface";

export type TransporterInfo = Partial<Omit<Transporter, 'id' | 'role'>>;

const emptyTransporterInfo: TransporterInfo = {
  name: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  ZIPCode: 0,
  country: "",
  companyName: "",
  SIRETNumber: 0,
};

const allowedFields: (keyof TransporterInfo)[] = [
  "name",
  "lastName",
  "email",
  "phone",
  "address",
  "ZIPCode",
  "country",
  "companyName",
  "SIRETNumber",
];

export const useTransporterInfo = () => {
  const [transporterInfo, setTransporterInfo] = useState<TransporterInfo>(emptyTransporterInfo);
  const [isAccountComplete, setIsAccountComplete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const storedInfo = authService.getUser();
    if (storedInfo) {
      setTransporterInfo(storedInfo);
      checkAccountCompletion(storedInfo);
    }
  }, []);

  const checkAccountCompletion = (info: TransporterInfo) => {
    const isComplete = allowedFields.every((field) => !!info[field]);
    setIsAccountComplete(isComplete);
  };

  const updateTransporterInfo = async (newInfo: Partial<TransporterInfo>) => {
    setIsLoading(true);
    setError(null);
    try {
      // Create a new object with only the allowed fields
      const filteredInfo = allowedFields.reduce((acc, field) => {
        if (newInfo[field] !== undefined) {
          // @ts-ignore
          acc[field] = newInfo[field];
        }
        return acc;
      }, {} as TransporterInfo);

      const updatedInfo = await authService.updateTransporter(filteredInfo);
      setTransporterInfo(updatedInfo);
      checkAccountCompletion(updatedInfo);
      return { success: true };
    } catch (err : any) {
      const errorMessage = "Il y a eu une erreur lors de la mise à jour des informations du transporteur";
      setError(`${errorMessage} : ${err.message}`);
      console.error("Error updating transporter info:", err);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return { transporterInfo, isAccountComplete, updateTransporterInfo, isLoading, error };
};
